import React, { useEffect, useMemo, useRef, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { WaChannel, WaState, WaTemplate } from "../../../services/v2/prospects.service";
import { PlantillaEnvio } from "../../../services/v2/plantillas.service";
import { errorTexto, faltan } from "./salesHelpers";
import Adjuntos from "./adjuntos";
import HorariosModal from "./horariosModal";
import UbicacionModal from "./ubicacionModal";
import { BotonEmoji, insertarEnCursor, teclaEnter } from "./equipoEmojis";
import { useCrecer } from "./crecer";
import { useBorrador } from "./borradores";
import AvisoMatriculaGratis, { prepararSonido } from "./avisoMatriculaGratis";

interface Props {
	campusId: number;
	prospectId: number;
	firstName: string;
	countryId: number | null;
	wa: { state: WaState; channels: WaChannel[] } | null;
	texto: string;                              // lo controla la ficha: "Enviar al chat" de Horarios escribe aquí
	setTexto: (t: string | ((antes: string) => string)) => void;
	ubicacion?: string | null;                  // ya no se usa: Ubicación abre su modal (ct_ubicaciones)
	onEnviado: () => void;
	onCanal: (channelId: number) => void;
	/** La ficha manda el modo: "+ Nota" y "WhatsApp" de arriba abren aquí con el cursor puesto. */
	modo: "mensaje" | "nota";
	setModo: (m: "mensaje" | "nota") => void;
	/** Sube cada vez que piden escribir desde arriba: pone el cursor en el cuadro. */
	foco?: number;
	/** Responder a un mensaje específico (la cita arriba, como en WhatsApp). */
	respondiendo?: { id: number; body: string | null; nuestro: boolean } | null;
	onCancelarRespuesta?: () => void;
	/** Enviar horarios: el plantel y el curso que le interesa, para abrir el catálogo ya filtrado. */
	plantelId?: number;
	cursoInteres?: string | null;
	/** Nombre del cliente en "Respondiendo a…". */
	nombreCliente?: string;
	/** Cliente ajeno abierto por una llamada (2026-10-18): solo nota, sin WhatsApp. */
	soloNota?: boolean;
	/** Mensaje de texto en camino: la conversación lo pinta al instante (con relojito) y lo quita cuando llega el de verdad. */
	onSale?: (m: { clave: number; body: string; cita: { id: number; body: string | null; nuestro: boolean } | null }) => void;
	/** Terminó el envío: eventId del mensaje guardado, o null si no salió. */
	onSalio?: (clave: number, eventId: number | null) => void;
}

let ultimaClave = 0;

/**
 * Abajo del chat (lienzo): Mensaje sale por WhatsApp; Nota se queda en WAPP. Dentro de las 24 h desde el último
 * mensaje de la persona se escribe libre; fuera, Meta solo deja mandar una plantilla aprobada.
 */
const Compositor: React.FC<Props> = ({ campusId, prospectId, firstName, countryId, wa, texto, setTexto, onEnviado, onCanal, modo: modoFicha, setModo: setModoFicha, foco, respondiendo, onCancelarRespuesta, plantelId, cursoInteres, nombreCliente, soloNota, onSale, onSalio }) => {
	const modo: "mensaje" | "nota" = soloNota ? "nota" : modoFicha;
	const setModo = (m: "mensaje" | "nota") => { if (!soloNota) setModoFicha(m); };
	const http = HttpManager.getInstance();
	const [plantillas, setPlantillas] = useState<WaTemplate[] | null>(null);
	const [deWapp, setDeWapp] = useState<PlantillaEnvio[]>([]);
	// Cuenta regresiva: cuándo vence la oferta para este cliente (hora local del navegador).
	const [vence, setVence] = useState<string>("");
	const [usarPlantilla, setUsarPlantilla] = useState<boolean>(false);
	const [tpl, setTpl] = useState<string>("");
	// Buscar en la lista de plantillas (Esteban, 2026-10-12: "la lista es enorme"): por nombre o por texto.
	const [buscaTpl, setBuscaTpl] = useState<string>("");
	const [params, setParams] = useState<string[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	// El mensaje salió pero le puso costo a la matrícula: aviso rojo que hay que cerrar (lo dice el servidor).
	const [matricula, setMatricula] = useState<string | null>(null);
	// La nota lleva su propio borrador: al volver a WhatsApp no se queda escrita en el cuadro del mensaje (y no se manda por error).
	const [nota, setNota] = useBorrador(prospectId ? "nota:" + prospectId : null);
	const caja = useRef<HTMLDivElement>(null);
	// Grabando una nota de voz o con un archivo en vista previa: el cuadro de texto se esconde.
	const [adjOcupado, setAdjOcupado] = useState<boolean>(false);
	const [verHorarios, setVerHorarios] = useState<boolean>(false);
	const [verUbicacion, setVerUbicacion] = useState<boolean>(false);
	// El cuadro crece con lo escrito (como WhatsApp): hasta ~6 renglones y luego se desplaza. La nota, hasta 12.
	const cuadro = useRef<HTMLTextAreaElement>(null);
	useCrecer(cuadro, (modo === "nota" ? "n" + nota : "m" + texto), modo === "nota" ? 12 : 6);

	const state = wa?.state;
	const canalId = state?.channelId ?? null;
	const abierta = !!state?.windowOpen;
	const conPlantilla = modo === "mensaje" && (usarPlantilla || !abierta);
	// Números por departamento (2026-10-12): el servidor ya manda solo los de quien escribe. Con la ventana abierta, candado.
	const candado = state?.candado || [];
	const canales = useMemo(() => (wa?.channels || []).filter((c) => candado.length ? candado.includes(c.id)
		: (!countryId || c.country_id === countryId || c.id === canalId)), [wa, countryId, canalId]); // eslint-disable-line react-hooks/exhaustive-deps
	const bloqueado = candado.length === 1 && candado[0] === canalId;
	const otraAbierta = (state?.abiertasEn || []).find((x) => (wa?.channels || []).some((c) => c.id === x.channelId)) || null;
	// Número en amarillo o rojo con WhatsApp (Esteban, 2026-10-15, MTY 8201): con la ventana cerrada no se manda plantilla desde ahí
	// (lo hunde más); se ofrece un número sano. Con la ventana abierta se contesta normal.
	const sinPlantillas = modo === "mensaje" && !abierta && !!state?.calidad;
	const sanos = sinPlantillas ? canales.filter((c) => c.id !== canalId && !c.calidad) : [];
	const sano = sanos.find((c) => c.campus_id === canales.find((x) => x.id === canalId)?.campus_id) || sanos[0] || null;
	const nombreCanal = (id: number) => { const c = (wa?.channels || []).find((x) => x.id === id); return c ? (c.label || c.name || c.display_phone) : "otro número"; };

	// "Escribiendo…" en el WhatsApp del cliente mientras la asesora escribe (Esteban, 2026-10-12). Una vez cada 15 s como mucho;
	// WhatsApp lo deja visible hasta 25 s o hasta que llega el mensaje. Si falla no se dice nada: es un adorno.
	const ultimoEscribiendo = useRef<number>(0);
	const avisarEscribiendo = (nuevo: string) => {
		if (modo !== "mensaje" || !abierta || !state?.canSend || nuevo.length <= texto.length) return;
		const ahora = Date.now();
		if (ahora - ultimoEscribiendo.current < 15000) return;
		ultimoEscribiendo.current = ahora;
		http.prospectsService.escribiendo(campusId, prospectId, canalId).catch(() => undefined);
	};
	useEffect(() => { ultimoEscribiendo.current = 0; }, [prospectId]);

	useEffect(() => { setUsarPlantilla(false); setTpl(""); setParams([]); setError(null); }, [prospectId]);
	// "+ Nota" se cierra con la X, Cancelar, Esc o tocando fuera (Esteban, 2026-10-03). Cerrar no borra lo escrito.
	const cerrarNota = () => { setModo("mensaje"); setError(null); };
	useEffect(() => {
		if (modo !== "nota") return;
		setError(null);   // el aviso de plantillas de WhatsApp no aplica a la nota
		const fuera = (e: MouseEvent | TouchEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) cerrarNota(); };
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape") cerrarNota(); };
		document.addEventListener("mousedown", fuera);
		document.addEventListener("touchstart", fuera);
		document.addEventListener("keydown", esc);
		return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("touchstart", fuera); document.removeEventListener("keydown", esc); };
	}, [modo]);
	useEffect(() => {
		setPlantillas(null);
		if (!canalId || !conPlantilla || sinPlantillas) return;
		http.prospectsService.waTemplates(campusId, canalId, prospectId).then((r) => { setPlantillas(r.data.templates); setDeWapp(r.data.wapp || []); })
			.catch((e) => setError(errorTexto(e)));
	}, [canalId, conPlantilla, sinPlantillas]);

	// Sin desplazar la página al poner el cursor (Responder, + Nota): en el iPhone la mandaba hacia arriba.
	useEffect(() => { if (foco) (document.getElementById("cv-escribir") as HTMLTextAreaElement | null)?.focus({ preventScroll: true }); }, [foco, modo]);

	// "w:12" = de la sección Plantillas (con quién la ve y datos que WAPP llena solo); "nombre|idioma" = de Meta.
	const pw = tpl.startsWith("w:") ? deWapp.find((x) => "w:" + x.id === tpl) || null : null;
	const plantilla = pw ? null : plantillas?.find((t) => t.name + "|" + t.language === tpl) || null;
	const sinAcento = (x: string) => x.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/_/g, " ");
	const qTpl = sinAcento(buscaTpl.trim());
	const pasa = (...campos: (string | null | undefined)[]) => !qTpl || qTpl.split(/\s+/).every((w) => campos.some((c) => c && sinAcento(c).includes(w)));
	const wappVista = deWapp.filter((t) => "w:" + t.id === tpl || pasa(t.nombre, t.meta_name, t.cuerpo));
	const metaVista = (plantillas || []).filter((t) => t.name + "|" + t.language === tpl || pasa(t.name, t.body, t.equipo));
	useEffect(() => {
		if (pw) {
			setParams(pw.variables.map((v) => v.valor || ""));
			if (pw.lto?.activa) {
				const d = new Date(Date.now() + (pw.lto.horas || 48) * 3600e3);
				const z = (n: number) => String(n).padStart(2, "0");
				setVence(`${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`);
			}
			return;
		}
		if (!plantilla) return;
		setParams(Array.from({ length: plantilla.params }, (_, i) => (i === 0 ? firstName : "")));
	}, [tpl]);
	const vista = pw
		? [pw.header_tipo === "text" && pw.header_texto ? "*" + pw.header_texto + "*" : "", pw.cuerpo, pw.pie || ""].filter(Boolean).join("\n\n")
			.replace(/\{\{\s*[a-z_]+\s*\}\}/g, ((i) => (m: string) => params[i++] || m)(0))
		: plantilla ? plantilla.body.replace(/\{\{(\d+)\}\}/g, (m, n) => params[Number(n) - 1] || m) : "";

	// Los textos se mandan en fila, uno tras otro: si escribe tres seguidos, le llegan al cliente en ese orden.
	const fila = useRef<Promise<void>>(Promise.resolve());
	const abiertoAhora = useRef<number>(prospectId);
	abiertoAhora.current = prospectId;

	const enviar = async () => {
		// Texto libre como WhatsApp (Esteban, 2026-10-18): el cuadro se vacía y el mensaje sale en la conversación al
		// instante; no se espera a Meta con el cuadro gris. Si no sale, el texto vuelve al cuadro (si quedó vacío) y se avisa.
		if (modo === "mensaje" && !conPlantilla) {
			const t = texto.trim();
			const cita = respondiendo || null;
			const clave = ++ultimaClave;
			const ponerTexto = setTexto;   // el de esta conversación, aunque para cuando falle ya esté abierta otra
			setError(null);
			prepararSonido();   // dentro del toque: el aviso de matrícula gratis llega después y ya puede sonar
			setTexto("");
			if (onCancelarRespuesta) onCancelarRespuesta();
			cuadro.current?.focus({ preventScroll: true });   // el teclado se queda abierto para el siguiente
			if (onSale) onSale({ clave, body: t, cita });
			fila.current = fila.current.then(() => http.prospectsService.sendWhatsapp(campusId, prospectId, { text: t, channelId: canalId, replyTo: cita?.id || null } as any)
				.then((r) => { if (onSalio) onSalio(clave, r.data?.event?.id ?? null); onEnviado(); if (r.data?.avisoMatricula) setMatricula(r.data.avisoMatricula); },
					(e) => { if (onSalio) onSalio(clave, null); if (abiertoAhora.current === prospectId) setError(errorTexto(e)); ponerTexto((antes) => (antes.trim() ? antes : t)); }));
			return;
		}
		setBusy(true); setError(null);
		prepararSonido();
		let r: { data: { avisoMatricula?: string | null } } | null = null;
		try {
			if (modo === "nota") {
				await http.prospectsService.note(campusId, prospectId, nota.trim(), "nota");
				setNota("");
				setModo("mensaje");
			} else if (conPlantilla) {
				if (pw) {
					r = await http.prospectsService.sendWhatsapp(campusId, prospectId, { plantillaId: pw.id, params, channelId: canalId,
						...(pw.lto?.activa && vence ? { vence: new Date(vence).toISOString() } : {}) } as any);
				} else {
					if (!plantilla) throw new Error("Elige una plantilla.");
					r = await http.prospectsService.sendWhatsapp(campusId, prospectId, { template: plantilla.name, language: plantilla.language, params, channelId: canalId });
				}
				setTpl(""); setUsarPlantilla(false);
			}
			onEnviado();
			if (r?.data.avisoMatricula) setMatricula(r.data.avisoMatricula);
		} catch (e) { setError(errorTexto(e)); }
		finally { setBusy(false); }
	};

	const puede = !busy && (modo === "nota" ? !!nota.trim()
		: !!state?.canSend && (conPlantilla ? !sinPlantillas && (!!plantilla || !!pw) && params.every((p) => p.trim()) : !!texto.trim()));

	return (
		<div ref={caja} className={"cv-compositor" + (modo === "nota" ? " en-nota" : "")}>
			{/* Una sola línea (Esteban, 2026-10-05): ventana, número, ubicación y plantilla juntos y angostos. */}
			<div className="cv-linea">
				{soloNota ? null : <div className="cv-modo" role="tablist" aria-label="Tipo">
					<button role="tab" aria-selected={modo === "mensaje"} className={"wa" + (modo === "mensaje" ? " on" : "")} onClick={() => setModo("mensaje")}>WhatsApp</button>
					<button role="tab" aria-selected={modo === "nota"} className={"nota" + (modo === "nota" ? " on" : "")} onClick={() => setModo("nota")}>+ Nota</button>
				</div>}
				{modo === "nota" ? <span className="cv-nota-tit">Nota interna: no le llega al cliente</span> : null}
				{modo === "nota" && !soloNota ? <button className="cv-cerrar-nota" onClick={cerrarNota} aria-label="Cerrar nota">×</button> : null}
				{modo === "mensaje" && state ? (
					<span className={"cv-ventana" + (abierta ? " abierta" : "")} title={abierta ? "Ventana de 24 h abierta: se escribe libre" : "Meta solo deja mandar una plantilla aprobada"}>
						{state.optOut ? "Pidió no recibir WhatsApp"
							: !canalId ? "Sin número de WhatsApp"
							: abierta ? <><span className="pt" aria-hidden="true" /><span className="cv-v-txt">Abierta · </span><span className="cv-v-largo">{faltan(state.windowClosesAt)}</span><span className="cv-v-corto">{faltan(state.windowClosesAt).replace(/ \d+ min$/, "")}</span></>
							: state.lastInboundAt ? "En este número pasaron 24 h: solo plantilla" : "A este número nunca le ha escrito: solo plantilla"}
					</span>
				) : null}
				{/* La ventana es POR NÚMERO (Esteban, 2026-10-06): si está abierta en otro número de WAPP, se ofrece cambiarse. */}
				{modo === "mensaje" && state && !abierta && !state.optOut && otraAbierta ? (
					<button className="btn-wa cv-chico cv-otra-ventana" onClick={() => onCanal(otraAbierta.channelId)}
						title={"Abierta en " + nombreCanal(otraAbierta.channelId) + " · " + faltan(otraAbierta.closesAt)}>
						Abierta en {nombreCanal(otraAbierta.channelId)}: usar ese número</button>
				) : modo === "mensaje" && state && !abierta && !state.optOut && state.woztell ? (
					<span className="cv-woztell" title="Ese número sigue en Woztell: contéstale desde Woztell o mándale plantilla desde aquí">
						Escribió por Woztell{state.woztell.canal ? " (" + state.woztell.canal + ")" : ""}</span>
				) : null}
				{modo === "mensaje" && bloqueado ? (
					<span className="cv-candado" title="La ventana de 24 h está abierta en este número: se contesta desde aquí">
						<svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true"><path fill="currentColor" d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zm-7-2a2 2 0 0 1 4 0v2h-4V7z" /></svg>
						<span>{nombreCanal(canalId as number)}</span>
					</span>
				) : modo === "mensaje" && canales.length > 1 ? (
					<select className="cv-numero" value={canalId || ""} onChange={(e) => onCanal(Number(e.target.value))} aria-label="Desde qué número"
						title={state?.desbloqueo || canales.find((c) => c.id === canalId)?.display_phone || ""}>
						{canales.map((c) => <option key={c.id} value={c.id}>{(c.label || c.name || c.display_phone) + (c.calidad && !abierta ? " · en " + c.calidad : "")}</option>)}
					</select>
				) : modo === "mensaje" && canalId ? <span className="cv-desde">{canales.find((c) => c.id === canalId)?.label || ""}</span> : null}
				{modo === "mensaje" && state?.desbloqueo ? <span className="cv-desbloqueo" title={state.desbloqueo}>Número libre: {state.desbloqueo.split(" (")[0]}</span> : null}
				{modo === "mensaje" && abierta ? (
					<span className="cv-rapidos">
						<button className="btn-wa cv-chico cv-ubic" onClick={() => setVerUbicacion(true)} title="Mandar la ubicación de una o varias academias (pin de mapa)">
							<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="currentColor" d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" /></svg>
							<span>Ubicación</span></button>
						<button className="btn-wa cv-chico cv-horarios-btn" onClick={() => setVerHorarios(true)} title="Mandar horarios del catálogo">
							<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="currentColor" d="M7 2h2v2h6V2h2v2h3a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3V2zm12 8H5v9h14v-9zM7 12h4v4H7v-4z" /></svg>
							<span>Horarios</span></button>
						<button className={"btn-wa cv-chico" + (usarPlantilla ? " pri" : "")} onClick={() => setUsarPlantilla(!usarPlantilla)}>Plantilla</button>
					</span>
				) : null}
			</div>

			{conPlantilla && sinPlantillas ? (
				<div className="cv-plantillas">
					<p className="cv-calidad">
						{nombreCanal(canalId as number)} está en {state!.calidad} con WhatsApp: desde este número no se mandan plantillas.
						{sano ? null : " Espera a que la persona escriba."}
					</p>
					{sano ? <button className="btn-wa cv-chico cv-otra-ventana" onClick={() => onCanal(sano.id)}>Usar {nombreCanal(sano.id)}</button> : null}
				</div>
			) : conPlantilla ? (
				<div className="cv-plantillas">
					{plantillas && plantillas.length + deWapp.length > 8 ? (
						<input type="search" className="cv-tpl-buscar" value={buscaTpl} onChange={(e) => setBuscaTpl(e.target.value)} placeholder="Buscar plantilla (nombre o texto)"
							aria-label="Buscar plantilla" />
					) : null}
					<select value={tpl} onChange={(e) => setTpl(e.target.value)} aria-label="Plantilla" disabled={!plantillas}>
						<option value="">{!plantillas ? "Cargando plantillas…" : qTpl ? (wappVista.length + metaVista.length ? (wappVista.length + metaVista.length) + " encontradas" : "Ninguna coincide") : "Elegir plantilla (" + (plantillas.length + deWapp.length) + ")"}</option>
						{/* Avisos (Utility) primero: cuestan unas 6 veces menos que una promoción; las promociones siguen ahí (Esteban, 2026-10-14). */}
						{[["UTILITY", "Avisos y seguimientos · más baratos"], ["MARKETING", "Promociones"]].map(([cat, titulo]) => {
							const w = wappVista.filter((t) => (t.categoria === "UTILITY") === (cat === "UTILITY"));
							const m = metaVista.filter((t) => (t.category === "UTILITY") === (cat === "UTILITY"));
							return w.length + m.length > 0 ? (
								<optgroup key={cat} label={titulo}>
									{w.map((t) => <option key={"w" + t.id} value={"w:" + t.id}>{t.nombre}{t.header_tipo === "image" ? " · foto" : t.header_tipo === "video" ? " · video" : t.header_tipo === "document" ? " · PDF" : ""}</option>)}
									{m.map((t) => <option key={t.name + t.language} value={t.name + "|" + t.language}>{t.name}{t.equipo ? " · " + t.equipo : ""}</option>)}
								</optgroup>
							) : null;
						})}
					</select>
					{plantilla || pw ? (
						<>
							{params.map((v, i) => {
								const campo = <input key={i} value={v} placeholder={pw ? pw.variables[i]?.etiqueta : "Dato " + (i + 1)} aria-label={pw ? pw.variables[i]?.etiqueta : "Dato " + (i + 1)}
									onChange={(e) => setParams(params.map((x, j) => (j === i ? e.target.value : x)))} />;
								// Las de la sección Plantillas dicen qué dato es cada cuadro (aunque ya venga lleno).
								return pw ? <div key={i} className="cv-dato"><span>{pw.variables[i]?.etiqueta}</span>{campo}</div> : campo;
							})}
							{pw?.lto?.activa ? (
								<div className="cv-dato"><span>⏰ Vence</span><input type="datetime-local" value={vence} onChange={(e) => setVence(e.target.value)} aria-label="Vence la oferta" /></div>
							) : null}
							<p className="cv-vista">{vista}</p>
							{!abierta && ((plantilla && plantilla.category === "MARKETING") || (pw && pw.categoria === "MARKETING")) ? (
								<p className="cv-costo">Promoción: cuesta unas 6 veces más que un Aviso. Si es un seguimiento, elige uno de “Avisos y seguimientos”.</p>
							) : null}
							{abierta ? <span className="cv-libre">Ventana abierta: sale como mensaje normal, sin costo de plantilla.</span> : null}
						</>
					) : null}
				</div>
			) : null}

			{respondiendo && modo === "mensaje" && !conPlantilla ? (
				<div className="eq-respondiendo cv-resp">
					<span><b>{respondiendo.nuestro ? "Respondiendo a tu mensaje" : "Respondiendo a " + (nombreCliente || firstName || "el cliente")}</b><small>{respondiendo.body || "Archivo"}</small></span>
					<button onClick={onCancelarRespuesta} aria-label="Cancelar respuesta">×</button>
				</div>
			) : null}
			<div className={"cv-escribir" + (adjOcupado ? " con-adjunto" : "") + (modo === "mensaje" && texto.trim() ? " escribiendo" : "")}>
				{modo === "mensaje" && !conPlantilla ? (
					<Adjuntos campusId={campusId} prospectId={prospectId} channelId={canalId} puede={!!state?.canSend && abierta}
						onEnviado={onEnviado} onOcupado={setAdjOcupado} onMatricula={setMatricula} />
				) : null}
				{adjOcupado && modo === "mensaje" ? null : !conPlantilla ? (<>
					<div className="wb-campo">
					<BotonEmoji enBarra onElegir={(em) => (modo === "nota" ? insertarEnCursor(cuadro.current, nota, em, setNota) : insertarEnCursor(cuadro.current, texto, em, setTexto))} disabled={busy}
						/* Stickers al cliente: solo con la ventana de 24 h abierta (Meta no deja mandar archivos fuera de ella). */
						stickers={modo === "mensaje" && !!state?.canSend && abierta ? { campusId, onEnviar: async (s) => {
							await http.prospectsService.whatsappSticker(campusId, prospectId, s.id, canalId);
							onEnviado();
						} } : undefined} />
					<textarea id="cv-escribir" ref={cuadro} rows={1} value={modo === "nota" ? nota : texto} onChange={(e) => { if (modo === "nota") setNota(e.target.value); else { avisarEscribiendo(e.target.value); setTexto(e.target.value); } }} aria-label={modo === "nota" ? "Nota" : "Mensaje"}
						placeholder={modo === "nota" ? "Escribe la nota" : "Mensaje"}
						onKeyDown={(e) => { teclaEnter(e, () => { if (puede) enviar(); }); }} /></div></>
				) : <div style={{ flex: 1 }} />}
				{modo === "nota" && !soloNota ? <button className="cv-cancelar" onClick={cerrarNota}>Cancelar</button> : null}
				{adjOcupado && modo === "mensaje" ? null : modo === "mensaje" && !conPlantilla ? (
					<button className="cv-enviar wa cv-avion" disabled={!puede} onClick={enviar} onMouseDown={(e) => e.preventDefault() /* no le quita el foco al cuadro: el teclado del celular no se cierra */} aria-label="Enviar" title="Enviar (Enter) · Salto de línea: Ctrl + Enter o Shift + Enter">
						{busy ? "…" : <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path fill="currentColor" d="M3.4 20.4 21 12 3.4 3.6 3.4 10l12.6 2-12.6 2z" /></svg>}
					</button>
				) : <button className={"cv-enviar " + (modo === "nota" ? "nota" : "wa")} disabled={!puede} onClick={enviar}>{busy ? "…" : modo === "nota" ? "Guardar nota" : abierta ? "Enviar" : "Enviar plantilla"}</button>}
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
			{verUbicacion ? (
				<UbicacionModal campusId={campusId} prospectId={prospectId} plantelId={plantelId || null} canalId={canalId}
					puedeEnviar={!!state?.canSend && abierta} onEnviado={onEnviado} onClose={() => setVerUbicacion(false)} />
			) : null}
			{verHorarios ? (
				<HorariosModal campusId={campusId} plantel={plantelId || campusId} curso={cursoInteres || null} puedeEnviar={!!state?.canSend && abierta}
					onClose={() => setVerHorarios(false)} onAlCuadro={(t) => { setModo("mensaje"); setTexto((texto ? texto + "\n\n" : "") + t); }}
					onEnviar={async (t) => {
						prepararSonido();
						const rh = await http.prospectsService.sendWhatsapp(campusId, prospectId, { text: t, channelId: canalId } as any);
						onEnviado();
						if (rh.data.avisoMatricula) setMatricula(rh.data.avisoMatricula);
					}} />
			) : null}
			{matricula !== null ? <AvisoMatriculaGratis fragmento={matricula} plantelId={plantelId || campusId} onCerrar={() => {
				setMatricula(null);
				(document.getElementById("cv-escribir") as HTMLTextAreaElement | null)?.focus({ preventScroll: true });
			}} /> : null}
		</div>
	);
};

export default Compositor;
